import React from "react";
import { MessageCircle, Heart, ShieldCheck, CheckCircle } from "lucide-react";
import ResponsiveImage from "./ResponsiveImage";
import { images } from "../data/images";
import { features } from "../data/mock";

const iconMap = {
  MessageCircle,
  Heart,
  ShieldCheck,
};

const Features = () => {
  return (
    <section id="over-ons" className="py-24 bg-gradient-to-br from-slate-50 to-sky-50" aria-labelledby="over-ons-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-4">
            Waarom Cleven Cleaning?
          </span>
          <h2 id="over-ons-heading" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-4">
            Waar staat het bedrijf voor?
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Wij geloven in transparantie, kwaliteit en klanttevredenheid. Ontdek
            wat ons anders maakt.
          </p>
        </div>

        {/* Company Fleet Photo */}
        <div className="relative rounded-3xl overflow-hidden mb-16 shadow-xl group">
          <ResponsiveImage
            image={images.vans}
            alt="Cleven Cleaning bedrijfsbussen in Rosmalen - volledig uitgerust wagenpark voor glasbewassing en schoonmaakdiensten in Noord-Brabant"
            sizes="(min-width: 1280px) 1280px, 100vw"
            className="w-full h-56 sm:h-72 lg:h-80 object-cover group-hover:scale-105 transition-transform duration-700"
            data-testid="features-fleet-photo"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6 sm:bottom-8 sm:left-8">
            <p className="text-white text-lg sm:text-2xl font-bold drop-shadow-lg">Ons professionele wagenpark</p>
            <p className="text-white/80 text-sm sm:text-base drop-shadow-md">Volledig uitgerust voor elk klus, groot of klein</p>
          </div>
        </div>

        {/* Local trust paragraph - SEO + lokale uitstraling */}
        <div className="max-w-3xl mx-auto text-center mb-16" data-testid="features-local-paragraph">
          <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
            Cleven Cleaning is uw lokale schoonmaakbedrijf in <strong>Rosmalen</strong> en omstreken.
            Wij werken dagelijks voor particulieren, VvE&apos;s en bedrijven in
            &apos;s&#8209;Hertogenbosch (Den Bosch), Vught, Berlicum, Heesch, Nuland, Uden, Empel en Hintham.
            Met vaste glazenwassers, modern materieel en heldere communicatie via ons CRM-systeem
            zorgen wij dat uw ramen, kozijnen en gevels altijd in topconditie zijn.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const IconComponent = iconMap[feature.icon];
            return (
              <div
                key={feature.id}
                className="relative bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 group"
              >
                {/* Number badge */}
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-sky-500 to-teal-500 rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg">
                  {index + 1}
                </div>

                {/* Icon */}
                <div className="w-16 h-16 bg-gradient-to-br from-sky-100 to-teal-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  {IconComponent && (
                    <IconComponent className="w-8 h-8 text-sky-600" />
                  )}
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-slate-800 mb-4">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>

                {/* Checkmarks */}
                <div className="mt-6 space-y-2">
                  {feature.id === 1 && (
                    <>
                      <div className="flex items-center text-slate-600">
                        <CheckCircle className="w-5 h-5 text-teal-500 mr-2 flex-shrink-0" />
                        <span>Minimaal 24 uur van tevoren bericht</span>
                      </div>
                      <div className="flex items-center text-slate-600">
                        <CheckCircle className="w-5 h-5 text-teal-500 mr-2 flex-shrink-0" />
                        <span>Modern CRM-systeem</span>
                      </div>
                    </>
                  )}
                  {feature.id === 2 && (
                    <>
                      <div className="flex items-center text-slate-600">
                        <CheckCircle className="w-5 h-5 text-teal-500 mr-2 flex-shrink-0" />
                        <span>100% tevredenheidsgarantie</span>
                      </div>
                      <div className="flex items-center text-slate-600">
                        <CheckCircle className="w-5 h-5 text-teal-500 mr-2 flex-shrink-0" />
                        <span>Snelle service bij klachten</span>
                      </div>
                    </>
                  )}
                  {feature.id === 3 && (
                    <>
                      <div className="flex items-center text-slate-600">
                        <CheckCircle className="w-5 h-5 text-teal-500 mr-2 flex-shrink-0" />
                        <span>Jarenlange ervaring</span>
                      </div>
                      <div className="flex items-center text-slate-600">
                        <CheckCircle className="w-5 h-5 text-teal-500 mr-2 flex-shrink-0" />
                        <span>Volledig verzekerd</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Features;
